import { ref } from 'vue';
export function useSwipeToAction(opts) {
    const { actionWidth } = opts;
    const threshold = opts.threshold ?? 60;
    const offsets = ref({});
    const swipingId = ref(null);
    const openId = ref(null);
    let startX = 0;
    let startY = 0;
    let moved = false;
    let prefetched = false;
    let hasPeeked = false;
    const offset = (id) => offsets.value[id] ?? 0;
    // --- Pointer-agnostic core (shared by touch + mouse) ---
    function begin(x, y, item) {
        startX = x;
        startY = y;
        moved = false;
        prefetched = false;
        swipingId.value = item.id;
        // Close any other open row.
        if (openId.value && openId.value !== item.id) {
            offsets.value = { ...offsets.value, [openId.value]: 0 };
            openId.value = null;
        }
    }
    function update(x, y, item) {
        const dx = x - startX;
        const dy = y - startY;
        if (Math.abs(dx) > 8)
            moved = true;
        // Let vertical scrolls through.
        if (Math.abs(dx) <= Math.abs(dy))
            return;
        if (!prefetched) {
            prefetched = true;
            opts.onOpen?.(item);
        }
        const base = openId.value === item.id ? -actionWidth : 0;
        let off = base + dx;
        if (off > 0)
            off = 0;
        if (off < -actionWidth)
            off = -actionWidth;
        offsets.value = { ...offsets.value, [item.id]: off };
    }
    // Snap open/closed after a drag release, based on how far it was pulled.
    function snap(item) {
        if (offset(item.id) <= -threshold) {
            offsets.value = { ...offsets.value, [item.id]: -actionWidth };
            openId.value = item.id;
            opts.onOpen?.(item);
        }
        else {
            offsets.value = { ...offsets.value, [item.id]: 0 };
            if (openId.value === item.id)
                openId.value = null;
        }
    }
    // Plain click/tap (no drag) — treat as a tap (open the row's detail).
    function tap(item) {
        offsets.value = { ...offsets.value, [item.id]: 0 };
        openId.value = null;
        opts.onTap?.(item);
    }
    function onTouchStart(e, item) {
        const touch = e.touches[0];
        if (!touch)
            return;
        begin(touch.clientX, touch.clientY, item);
    }
    function onTouchMove(e, item) {
        const touch = e.touches[0];
        if (!touch)
            return;
        update(touch.clientX, touch.clientY, item);
    }
    function onTouchEnd(_e, item) {
        swipingId.value = null;
        if (!moved)
            tap(item);
        else
            snap(item);
    }
    // Click-to-reveal: toggle a row's actions open/closed (no drag involved).
    function toggle(item) {
        if (openId.value === item.id) {
            offsets.value = { ...offsets.value, [item.id]: 0 };
            openId.value = null;
            return;
        }
        // Close any other open row first.
        if (openId.value)
            offsets.value = { ...offsets.value, [openId.value]: 0 };
        offsets.value = { ...offsets.value, [item.id]: -actionWidth };
        openId.value = item.id;
        opts.onOpen?.(item);
    }
    // One-time visual peek (open then closed) hinting the row is swipeable. The
    // actions stay mounted through the close slide (unmounting them at the same
    // instant the row starts sliding back makes them pop out — looks like a snap).
    function peek(item) {
        if (hasPeeked || !item?.id)
            return;
        hasPeeked = true;
        const id = item.id;
        // Prefetch the row's action data now so it's ready by the time the peek
        // opens (~500ms later).
        opts.onOpen?.(item);
        setTimeout(() => {
            offsets.value = { ...offsets.value, [id]: -actionWidth };
            openId.value = id;
            setTimeout(() => {
                offsets.value = { ...offsets.value, [id]: 0 };
                setTimeout(() => {
                    if (openId.value === id)
                        openId.value = null;
                }, 350);
            }, 600);
        }, 500);
    }
    return { offset, swipingId, openId, onTouchStart, onTouchMove, onTouchEnd, toggle, peek };
}
